import React, { useCallback, useMemo } from "react";

import {
  Container,
  Divider,
  EmptyContainer,
  HeaderContainer,
  HeaderTitle,
  Search,
  Select,
  Text,
} from "./styles";
import { FormattedMessage, useIntl } from "react-intl";
import AnimatedMainContainer from "../../components/AnimatedMainContainer";
import SearchEvent from "./components/search-event";
import Button from "./components/Button";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../../hooks/toast";
import moment from "moment";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";
import api from "../../service";
import generateHeaders from "../../utils/generateHeaders";
import ReportTable from "./components/ReportTable";
import { useState } from "react";
import { useEffect } from "react";
import { debounce } from "lodash";
import {
  getMediaKitReportAdmin,
  updateMediaKitStatus,
} from "../../redux/modules/AI_Creator/actions";

const DEFAULT_PAGE = 1;
const DEFAULT_ROWS_PER_PAGE = 10;

export default function AICreatorReport() {
  const intl = useIntl();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const dispatch = useDispatch();
  const { reportData, pageInfo } = useSelector((state) => state.ai_creator);

  const parseSelectedStatus = useCallback(() => {
    switch (selectedStatus) {
      default:
      case "all":
        return "";

      case "created":
        return "true";

      case "pending":
        return "false";
    }
  }, [selectedStatus]);

  const filters = useMemo(() => {
    return {
      eventId: selectedEvent,
      year: selectedYear,
      status: parseSelectedStatus(),
      sortBy,
      order: sortDirection,
      search,
    };
  }, [
    parseSelectedStatus,
    search,
    selectedEvent,
    selectedYear,
    sortBy,
    sortDirection,
  ]);

  const fetchData = useCallback(() => {
    if (!selectedYear || !selectedEvent) {
      return undefined;
    }

    const params = {
      ...filters,
      page: currentPage - 1,
      per_page: rowsPerPage,
    };

    return dispatch(getMediaKitReportAdmin(params));
  }, [
    currentPage,
    dispatch,
    filters,
    rowsPerPage,
    selectedEvent,
    selectedYear,
  ]);

  const handleSearch = useCallback((searchTerm) => {
    setSearch(searchTerm);
    setCurrentPage(DEFAULT_PAGE);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
    setSortBy("");
    setSortDirection("");
  }, []);

  const handleUpdateMediaKitStatus = useCallback((opportunityId, created, origin) => {
    const params = {
      opportunityId: opportunityId,
      created: created,
      origin: origin,
    };

    dispatch(updateMediaKitStatus(params));
  }, [dispatch]);

  useEffect(() => {
    if (pageInfo) {
      setTotalRows(pageInfo.totalElements);
    }
  }, [pageInfo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <AnimatedMainContainer
      breadcrumb={intl.formatMessage({
        id: "breadcumb.ai_creator_report",
      })}
    >
      <Container>
        <SearchEvent
          setSelectedEvent={setSelectedEvent}
          setSelectedYear={setSelectedYear}
        />

        <Divider />

        {selectedYear && selectedEvent ? (
          <>
            <Header
              params={filters}
              setSearch={handleSearch}
              setSelectedStatus={setSelectedStatus}
              disableDownload={reportData.length === 0}
            />

            <ReportTable
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              totalRows={totalRows}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              reportData={reportData}
              updateMediaKitStatus={handleUpdateMediaKitStatus}
            />
          </>
        ) : (
          <EmptyContainer>
            <Text>
              <FormattedMessage
                id={
                  selectedYear
                    ? "report_filter_event_required"
                    : "report_filter_year_required"
                }
              />
            </Text>
          </EmptyContainer>
        )}
      </Container>
    </AnimatedMainContainer>
  );
}

function Header({ params, setSearch, setSelectedStatus, disableDownload }) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState("");

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
      }, 500),
    [setSearch],
  ); // Memoize to prevent recreation on each render

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  const options = useMemo(
    () => [
      { value: "all", label: <FormattedMessage id={"all"} /> },
      { value: "created", label: <FormattedMessage id={"created"} /> },
      { value: "pending", label: <FormattedMessage id={"pending"} /> },
    ],
    [],
  );

  const handleChange = (value) => {
    setSelectedStatus(value);
  };

  return (
    <HeaderContainer>
      <div>
        <HeaderTitle>
          <FormattedMessage id={"results"} />
        </HeaderTitle>

        <Select
          style={{ width: 120 }}
          defaultValue={"all"}
          onChange={handleChange}
          options={options}
        />
      </div>
      <div>
        <DownloadReport params={params} disabled={disableDownload} />
        <Search
          size="large"
          placeholder={intl.formatMessage({ id: "search" })}
          prefix={<SearchIcon />}
          value={inputValue}
          onChange={handleSearchChange}
        />
      </div>
    </HeaderContainer>
  );
}

function DownloadReport({ params, disabled }) {
  const intl = useIntl();
  const { token } = useSelector((state) => state.auth);
  const { addToast } = useToast();

  const parseToCVS = useCallback(
    ({ opportunityMediaKits }) => {
      if (opportunityMediaKits.length === 0) return;

      const columns = [
        [
          intl.formatMessage({ id: "contract" }),
          intl.formatMessage({ id: "company_name" }),
          intl.formatMessage({ id: "email" }),
          intl.formatMessage({ id: "admin_ai_creator.origin" }),
          intl.formatMessage({ id: "admin_ai_creator.status" }),
        ],
      ];

      const cleanedUsers = opportunityMediaKits.map(
        ({ contract, created, email, name, origin }) => ({
          contract: contract ?? "-",
          name,
          email: email ?? "-",
          origin: intl.formatMessage({ id: origin }) ?? "-",
          created: intl.formatMessage({ id: created ? "created" : "pending" }),
        }),
      );

      const worksheet = XLSX.utils.json_to_sheet(cleanedUsers);
      XLSX.utils.sheet_add_aoa(worksheet, columns, { origin: "A1" });

      const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
      const csvBlobData = new Blob([String.fromCharCode(0xfeff), csvOutput], {
        type: "text/csv;charset=utf-8",
      });
      const timestamp = moment().format("YYYY-MM-DD_HH-mm-ss");
      FileSaver.saveAs(csvBlobData, `Analytics-Courses-${timestamp}.csv`);
    },
    [intl],
  );

  const handleDownload = useCallback(async () => {
    try {
      const headers = generateHeaders(token);
      const options = {
        ...headers,
        params: removeUndefined({
          event_id: params.eventId,
          year: params.year,
          created: params.status,
          search: params.search,
          sortBy: params.sortBy,
          order: params.order,
          fetch_all: true,
        }),
      };

      const { data } = await api.get("/media-kit/admin", options);
      const { opportunityMediaKits } = data;

      parseToCVS({ opportunityMediaKits });

      addToast({
        type: "success",
        title: intl.formatMessage({
          id: "ai_creator.download_success",
        }),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: intl.formatMessage({
          id: "ai_creator.download_failure",
        }),
      });
    }
  }, [
    token,
    params.year,
    params.eventId,
    params.status,
    params.search,
    params.sortBy,
    params.order,
    parseToCVS,
    addToast,
    intl,
  ]);

  return (
    <Button
      label={"download_report"}
      size={"md"}
      theme={"secondary"}
      Icon={"report"}
      onClick={handleDownload}
      disabled={disabled}
    />
  );
}

const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="19"
    height="18"
    fill="none"
    viewBox="0 0 19 18"
  >
    <path
      stroke="#585F74"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M9.425 15.75a7.125 7.125 0 1 0 0-14.25 7.125 7.125 0 0 0 0 14.25M17.3 16.5 15.8 15"
    ></path>
  </svg>
);

const removeUndefined = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([_, value]) => value));
